import { Col, type ColProps } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/fields/InputField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';

const defaultColSpan = { lg: 8, md: 12, xs: 24 };

export type UobBankIntegrationProps = {
    prefix?: string;
    colSpan?: ColProps;
    disabled?: boolean;
};

const fieldNames = [
    'host',
    'allowedIps',
    'clientId',
    'applicationId',
    'apiKey',
    'sslPrivateKey',
    'sslPublicCert',
    'encryptionPrivateKey',
    'encryptionPublicCert',
    'signaturePrivateKey',
    'dealerCode',
] as const;

const UobBankIntegration = ({ prefix, colSpan = defaultColSpan, disabled = false }: UobBankIntegrationProps) => {
    const { t } = useTranslation(['bankDetails']);

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    return (
        <>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.allowedIps', { returnObjects: true })}
                    disabled={disabled}
                    name={names.allowedIps}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.uobIntegration.host', { returnObjects: true })}
                    disabled={disabled}
                    name={names.host}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.uobIntegration.clientId', { returnObjects: true })}
                    disabled={disabled}
                    name={names.clientId}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.uobIntegration.applicationId', { returnObjects: true })}
                    disabled={disabled}
                    name={names.applicationId}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.uobIntegration.apiKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.apiKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.uobIntegration.sslPrivateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.sslPrivateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.uobIntegration.sslPublicCert', { returnObjects: true })}
                    disabled={disabled}
                    name={names.sslPublicCert}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.uobIntegration.encryptionPrivateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.encryptionPrivateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.uobIntegration.encryptionPublicCert', { returnObjects: true })}
                    disabled={disabled}
                    name={names.encryptionPublicCert}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <SingleSecretUploadField
                    {...t('bankDetails:fields.uobIntegration.signaturePrivateKey', { returnObjects: true })}
                    disabled={disabled}
                    name={names.signaturePrivateKey}
                    required
                />
            </Col>
            <Col {...colSpan}>
                <InputField
                    {...t('bankDetails:fields.bankIntegration.dealerCodeForAudiAndSDM', { returnObjects: true })}
                    disabled={disabled}
                    name={names.dealerCode}
                />
            </Col>
        </>
    );
};

export default UobBankIntegration;
